/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import NumberField from "../inputs/NumberField";
import TextField from "../inputs/TextField";
import Form from "../../ui/Form/Form";
import CopyButton from "../buttons/CopyButton";
import {
  FORM_VALIDATION_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";
import DropdownField from "../inputs/DropdownField";
import ToggleField from "../inputs/ToggleField";

interface IProps {
  esRef: string;
  data: any;
  setData: any;
  isEditing: boolean;
  formFields: any;
}

const EditDFSIConnectionRowFormFields = ({
  esRef,
  data,
  setData,
  isEditing,
  formFields,
}: IProps) => {
  return (
    <Form>
      <div className="readonly-data" data-cy="readonly-section">
        <ul>
          <li className="flex items-center" data-cy="referenceID">
            <h5>
              {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["Reference ID"]}
            </h5>
            <div className="data-content">
              <span
                className={(data.Ref || esRef).length > 20 ? "truncate" : ""}
              >
                {data.Ref || esRef}
              </span>
              <CopyButton copyContent={data.Ref || esRef} />
            </div>
          </li>
          {data.type ? (
            <li className="flex items-center" data-cy="translationType">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "Translation Type"
                  ]
                }
              </h5>
              <div className="data-content">
                <span className={data.type.length > 20 ? "truncate" : ""}>
                  {data.type}
                </span>
                <CopyButton copyContent={data.type} />
              </div>
            </li>
          ) : null}

          <li className="flex items-center" data-cy="CreationDate">
            {data.CreationDate ? (
              <div className="flex items-center mr-3">
                <h5>
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .CreationDate
                  }
                </h5>
                <div className="data-content">
                  <span> {data.CreationDate} </span>
                </div>
              </div>
            ) : null}

            {data.ModificationDate && (
              <div className="flex items-center" data-cy="ModificationDate">
                <h5>
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .ModificationDate
                  }
                </h5>
                <div className="data-content">
                  <span> {data.ModificationDate} </span>
                </div>
              </div>
            )}
          </li>

          {data["CONNECTION-ID"] ? (
            <li className="flex items-center " data-cy="connectionID">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "CONNECTION-ID"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {data["CONNECTION-ID"]} </span>
                <CopyButton copyContent={data["CONNECTION-ID"]} />
              </div>
            </li>
          ) : null}

          {data["MCPTT-ID"] ? (
            <li className="flex items-center " data-cy="MCPTT_ID">
              <h5>
                {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["MCPTT-ID"]}
              </h5>
              <div className="data-content">
                <span> {data["MCPTT-ID"]} </span>
                <CopyButton copyContent={data["MCPTT-ID"]} />
              </div>
            </li>
          ) : null}

          {data["DFSI-VERSION"] ? (
            <li className="flex items-center " data-cy="dfsiVersion">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "DFSI-VERSION"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {data["DFSI-VERSION"]} </span>
                <CopyButton copyContent={data["DFSI-VERSION"]} />
              </div>
            </li>
          ) : null}

          {data["IWF-ROLE"] ? (
            <li className="flex items-center " data-cy="dfsiVersion">
              <h5>
                {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["IWF-ROLE"]}
              </h5>
              <div className="data-content">
                <span> {data["IWF-ROLE"]} </span>
              </div>
            </li>
          ) : null}
        </ul>
      </div>

      {formFields.map(
        (rows: {
          cols: number;
          fields: {
            name: string;
            tooltipText?: string;
            label: string;
            type: string;
            invalidText?: string | null;
            isInputInvalid?: ({
              version,
              role,
              val,
            }: {
              version?: number;
              role?: string;
              val?: string | number;
            }) => boolean;
            min?: number;
            max?: number;
            disabled?: (dfsiVersion?: number, role?: string) => boolean;
            required?: boolean;
            items?: string[];
          }[];
        }) => {
          return (
            <div className={`grid grid-cols-${rows.cols} gap-4 form-control`}>
              {rows.fields.map((field) => {
                switch (field.type) {
                  case "TOGGLE":
                    return (
                      <ToggleField
                        labelText={field.label}
                        onChange={(val) =>
                          setData({
                            ...data,
                            [field.name]: val,
                          })
                        }
                        value={data[field.name] as boolean}
                        tooltipText={field.tooltipText}
                        defaultToggled
                      />
                    );
                  case "NUMBER":
                    return (
                      <NumberField
                        onChange={(val) => {
                          if (!val || !Number.isNaN(+val))
                            setData({
                              ...data,
                              [field.name]: val ? +val : undefined,
                            });
                        }}
                        disabled={
                          !isEditing ||
                          field.disabled?.(
                            data["DFSI-VERSION"],
                            data["IWF-ROLE"]
                          )
                        }
                        label={field.label}
                        placeholder={field.label}
                        max={field.max || 0}
                        min={field.min || 0}
                        invalid={
                          !isEditing
                            ? false
                            : field.isInputInvalid?.({
                                version: data["DFSI-VERSION"],
                                role: data["IWF-ROLE"],
                                val: data[field.name] as number,
                              })
                        }
                        invalidText={
                          field.invalidText ||
                          `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                        }
                        value={data[field.name] as number}
                        tooltipText={field.tooltipText}
                        required
                      />
                    );
                  case "DROPDOWN":
                    return (
                      <DropdownField
                        className="dropdown-field"
                        label={field.label}
                        selectedItem={data[field.name]}
                        items={field.items}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) =>
                          field.name === "DFSI-VERSION"
                            ? setData({
                                ...data,
                                [field.name]: +selectedItem,
                              })
                            : setData({
                                ...data,
                                [field.name]: selectedItem,
                              })
                        }
                        tooltipText={field.tooltipText}
                        disabled={
                          !isEditing || field.disabled?.(data["DFSI-VERSION"])
                        }
                        required
                      />
                    );
                  default:
                    return (
                      <TextField
                        onChange={(val) =>
                          setData({
                            ...data,
                            [field.name]: val,
                          })
                        }
                        disabled={
                          !isEditing ||
                          field.disabled?.(
                            data["DFSI-VERSION"],
                            data["IWF-ROLE"]
                          )
                        }
                        placeholder={field.label}
                        value={data[field.name] as string}
                        invalid={
                          !isEditing
                            ? false
                            : field.isInputInvalid?.({
                                version: data["DFSI-VERSION"],
                                val: data[field.name] as string,
                                role: data["IWF-ROLE"],
                              })
                        }
                        invalidText={
                          field.invalidText ||
                          FORM_VALIDATION_TRANSLATIONS.required(field.label)
                        }
                        labelText={field.label}
                        tooltipText={field.tooltipText}
                        required
                      />
                    );
                }
              })}
            </div>
          );
        }
      )}
    </Form>
  );
};

export default EditDFSIConnectionRowFormFields;
